import React, { useState, useEffect } from "react";

/* 
  Wrapper component which finds links to mode embeds within 
  its children (which should be flagged with `class="mode-embed"`)
  and fashions them into mode embed iframes.
  
  Code pulled from https://modeanalytics.com/embed/embed.js and
  transcribed to react. 

  Previously, every mode embed in a blog post had to have the above file 
  included as a script in every article that had an embed, ie:
  
  <a href="https://modeanalytics.com/modeanalytics/reports/89e835d20ab4/embed" class="mode-embed">
    Mode Analytics
  </a>
  <script src="https://modeanalytics.com/embed/embed.js"></script>

  Sample usage with this component:
  <ModeEmbed>
  {
    children which could contain mode embed content, ie:
    <a href="https://modeanalytics.com/modeanalytics/reports/89e835d20ab4/embed" class="mode-embed">Mode Analytics</a>
  }
  </ModeEmbed>


*/
const ModeEmbed = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const generateUUID = () => {
    const d = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      // eslint-disable-next-line no-bitwise
      const r = (d + Math.random() * 16) % 16 | 0;
      // d = Math.floor(d / 16);
      // eslint-disable-next-line no-bitwise
      return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
    });
    return uuid;
  };

  const getLinks = () => {
    const links = [];
    const docLinks = document.getElementsByTagName("a");
    const { length } = docLinks;

    for (let i = 0; i < length; i += 1) {
      if (` ${docLinks[i].className}`.indexOf(" mode-") !== -1) {
        links.push(docLinks[i]);
      }
    }
    return links;
  };

  const embed = link => {
    const id = generateUUID();
    const reportId = link.href.match(/reports\/(.*?)\//)[1];
    const iframe = document.createElement("iframe");
    const query = `resize=true&embed_id=${id}`;
    const url =
      link.href.indexOf("?") > 0
        ? `${link.href}&${query}`
        : `${link.href}?${query}`;

    iframe.src = url;
    iframe.style.border = "none";
    iframe.style.width = "100%";
    iframe.style.height = "300px";
    iframe.id = id;
    iframe.setAttribute("data-report", reportId);
    link.parentNode.replaceChild(iframe, link);

    // Setting iframe height to accomodate content height plus footer
    const onmessage = (event, currentId) => {
      if (event.data && event.data.id === currentId) {
        // footer = 24px height + 1px border
        const IFRAME_FOOTER_HEIGHT = 26;
        // Find the element again since if a framework like angular compiles our iframe, we'll be pointing to a placeholder frame
        document.getElementById(currentId).style.height = `${event.data.height +
          IFRAME_FOOTER_HEIGHT}px`;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", e => onmessage(e, id), false);
    } else {
      window.attachEvent("onmessage", e => onmessage(e, id));
    }
  };

  // when dom ready
  const loadEmbed = () => {
    const links = getLinks();
    const { length } = links;
    let className = "";

    for (let i = 0; i < length; i += 1) {
      className = ` ${links[i].className} `;
      if (className.indexOf(" mode-embed ") !== -1) {
        embed(links[i]);
      }
    }
  };

  // using effect to test when render has occured
  useEffect(() => {
    if (!isLoaded) {
      loadEmbed();
    } else {
      setIsLoaded(true);
    }
  }, []);

  return <>{children}</>;
};

export default ModeEmbed;
